/**
 * Brand ("TU" / tu.biz) helpers.
 *
 * Used by `lib/branded-head.js`, which wraps `next/head` so every <title>,
 * description, keywords and og/twitter title gets the brand automatically, and
 * by `scripts/check-brand-titles.mjs`. All functions are idempotent: running a
 * value that already carries the brand through them returns the same value.
 *
 * Note: "tu" is also the Spanish word for "your", so a bare "TU" search will
 * rarely rank. The brand is aimed at brand queries ("tu.biz", "TU Technical
 * Union").
 */

export const BRAND_SHORT = "TU";
export const BRAND_NAME = "Technical Union";
export const BRAND_FULL = "TU - Technical Union";
/** Name Google should print above each result. Page titles still use BRAND_FULL. */
export const BRAND_SITE_NAME = "TU | Technical Union";
export const BRAND_DOMAIN = "tu.biz";

/** Brand terms every keywords tag should include. */
export const BRAND_KEYWORDS = [BRAND_SHORT, BRAND_NAME, BRAND_DOMAIN];

/** Longest title we try to keep (Google truncates around 60 characters). */
export const TITLE_SOFT_LIMIT = 70;
export const DESCRIPTION_LIMIT = 160;

const HOME_PATHS = new Set(["/", "/en", "/es"]);

const BRAND_RE = "(?:TU\\s*[-\u2013\u2014|]\\s*Technical\\s+Union|Technical\\s+Union|TU)";
const LEADING_BRAND = new RegExp(`^\\s*${BRAND_RE}\\s*[|\\-\u2013\u2014:]\\s*`);
const TRAILING_BRAND = new RegExp(`\\s*[|\\-\u2013\u2014:]\\s*${BRAND_RE}\\s*$`);
const MENTIONS_BRAND = /\bTU\b|Technical\s+Union|tu\.biz/;

function collapse(value) {
	return String(value ?? "")
		.replace(/\s+/g, " ")
		.trim();
}

function isBlank(value) {
	const v = collapse(value);
	return !v || v === "undefined" || v === "null";
}

export function isHomePath(pathname) {
	return HOME_PATHS.has(pathname);
}

/** Title text without any brand prefix / suffix. */
export function stripBrand(title) {
	let out = collapse(title);
	for (let i = 0; i < 3; i += 1) {
		const next = out.replace(LEADING_BRAND, "").replace(TRAILING_BRAND, "");
		if (next === out) break;
		out = next;
	}
	if (
		out === BRAND_SHORT ||
		out === BRAND_NAME ||
		out === BRAND_FULL ||
		out === BRAND_SITE_NAME
	) {
		return "";
	}
	return out;
}

/**
 * Page title: "<page> | TU - Technical Union" (falls back to "<page> | TU" when
 * the long form would be over the soft limit). Home pages stay brand-first.
 */
export function brandTitle(title, { isHome = false } = {}) {
	if (isBlank(title)) return title;

	if (isHome) {
		const text = collapse(title);
		if (text === BRAND_SHORT || text === BRAND_NAME || text === BRAND_FULL) {
			return BRAND_SITE_NAME;
		}
		return /^TU\b/.test(text) ? text : `${BRAND_FULL} | ${text}`;
	}

	const core = stripBrand(title).replace(/[\s.]+$/, "");
	if (!core) return BRAND_FULL;

	const full = `${core} | ${BRAND_FULL}`;
	return full.length <= TITLE_SOFT_LIMIT ? full : `${core} | ${BRAND_SHORT}`;
}

/** Appends the brand to a description when it is missing and still fits. */
export function brandDescription(description) {
	if (isBlank(description)) return description;
	const text = collapse(description);
	if (MENTIONS_BRAND.test(text)) return text;

	const withBrand = `${text.replace(/[\s.]+$/, "")}. ${BRAND_FULL}.`;
	return withBrand.length <= DESCRIPTION_LIMIT ? withBrand : text;
}

/** Makes sure "TU", "Technical Union" and "tu.biz" are in a keywords list. */
export function brandKeywords(keywords) {
	if (isBlank(keywords)) return keywords;
	const list = String(keywords)
		.split(",")
		.map((k) => k.trim().replace(/\.+$/, ""))
		.filter(Boolean);
	const have = new Set(list.map((k) => k.toLowerCase()));
	for (const term of BRAND_KEYWORDS) {
		if (!have.has(term.toLowerCase())) list.push(term);
	}
	return list.join(", ");
}
